import React, { useState, useEffect, useRef } from 'react';
import { Bot, Workflow, RefreshCw, Clock, Database, Network, Zap, Server, ChevronRight, Settings, FileCode, BarChart2, Layers, Repeat, LineChart } from 'lucide-react';
import { WorkflowSVG, SchedulingSVG, MonitoringSVG } from './AutomationSolutionSVGs';

const solutions = [
    {
        id: "workflow",
        title: "Workflow",
        icon: <Workflow className="w-12 h-12 text-blue-500" />,
        label: "Streamline Business Processes",
        sublabel: "Transform repetitive manual tasks into efficient automated workflows. Our intelligent workflow solutions learn from your processes to increase productivity and reduce human error.",
        color: "from-blue-600 to-blue-400",
        borderColor: "border-blue-500",
        shadowColor: "shadow-blue-500/20",
        link: "/automation-solutions/workflow",
        points: [
            "Convert manual processes into streamlined digital workflows",
            "Reduce operational costs through end-to-end automation",
            "Implement self-optimizing workflows that improve over time"
        ],
        tags: [
            { icon: <Bot />, text: "Process Automation" },
            { icon: <Workflow />, text: "Business Rules Engine" },
            { icon: <Zap />, text: "Operational Efficiency" }
        ],
        animDelay: "0s",
        ProcessSVG: WorkflowSVG
    },
    {
        id: "scheduling",
        title: "Scheduling",
        icon: <Clock className="w-12 h-12 text-amber-500" />,
        label: "Optimize Resource Allocation",
        sublabel: "Maximize operational efficiency with intelligent scheduling that adapts to your business needs. Our scheduling solutions optimize resource allocation and automate timing-based tasks.",
        color: "from-amber-600 to-amber-400",
        borderColor: "border-amber-500",
        shadowColor: "shadow-amber-500/20",
        link: "/automation-solutions/scheduling",
        points: [
            "Automate recurring tasks with precise timing control",
            "Dynamically allocate resources based on real-time demands",
            "Eliminate scheduling conflicts with predictive optimization"
        ],
        tags: [
            { icon: <Clock />, text: "Time-Based Automation" },
            { icon: <RefreshCw />, text: "Recurring Tasks" },
            { icon: <BarChart2 />, text: "Resource Optimization" }
        ],
        animDelay: "2s",
        ProcessSVG: SchedulingSVG
    },
    {
        id: "monitoring",
        title: "Monitoring",
        icon: <LineChart className="w-12 h-12 text-purple-500" />,
        label: "Intelligent Process Oversight",
        sublabel: "Gain real-time visibility into all your automated processes. Our monitoring solutions provide actionable insights, automated alerts, and self-healing capabilities for continuous operational excellence.",
        color: "from-purple-600 to-purple-400",
        borderColor: "border-purple-500",
        shadowColor: "shadow-purple-500/20",
        link: "/automation-solutions/monitoring",
        points: [
            "Track performance metrics with real-time dashboards and alerts",
            "Implement predictive analytics to prevent process failures",
            "Enable self-healing automation with intelligent error recovery"
        ],
        tags: [
            { icon: <Server />, text: "Performance Analytics" },
            { icon: <Repeat />, text: "Automated Recovery" },
            { icon: <LineChart />, text: "Process Intelligence" }
        ],
        animDelay: "4s",
        ProcessSVG: MonitoringSVG
    }
];

// Animation for flowing data in the pipeline
const PipelineFlow = ({ active }) => {
    return (
        <div className="absolute inset-0 overflow-hidden">
            {[...Array(15)].map((_, i) => (
                <div
                    key={i}
                    className={`absolute h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 
                              ${active ? 'animate-flow-down' : 'opacity-0'}`}
                    style={{
                        left: '50%',
                        top: `-10%`,
                        animationDelay: `${i * 0.5}s`,
                        animationDuration: '3s',
                    }}
                ></div>
            ))}
        </div>
    );
};

const SolutionCard = ({ solution, active, onNavigate, onMouseEnter }) => {
    return (
        <div
            className={`group relative overflow-hidden bg-gradient-to-b from-slate-800/80 to-slate-900/80 rounded-xl p-8 border transition-all duration-300 
            ${active ? solution.borderColor + ' ' + solution.shadowColor : 'border-slate-700/50'} 
            hover:${solution.borderColor} hover:${solution.shadowColor} cursor-pointer`}
            onClick={() => onNavigate(solution.link)}
            onMouseEnter={onMouseEnter}
        >
            {/* Simple hover effect */}
            <div className={`absolute inset-0 bg-gradient-to-br ${solution.color} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}></div>

            <div className="flex gap-6">
                {/* Left Content */}
                <div className="flex-1 relative z-10 max-w-[60%]">
                    <div className="flex items-center gap-6 mb-8">
                        {/* Icon with animated background */}
                        <div className={`relative w-20 h-20 rounded-full border-4 ${active ? solution.borderColor : 'border-slate-700'}`}>
                            <div className={`absolute inset-0 rounded-full bg-gradient-to-r ${solution.color} opacity-10 ${active ? 'animate-pulse' : ''}`} style={{ animationDuration: '3s' }}></div>
                            <div className="absolute inset-0 flex items-center justify-center">
                                {solution.icon}
                            </div>
                        </div>

                        <div>
                            <h3 className="text-3xl font-bold text-white tracking-wide mb-2">{solution.title}</h3>
                            <p className="text-lg text-white brightness-150 leading-relaxed">{solution.label}</p>
                        </div>
                    </div>

                    <div className="space-y-6">
                        <p className="text-lg text-slate-300 leading-relaxed">{solution.sublabel}</p>

                        {/* Points */}
                        {solution.points && solution.points.length > 0 && (
                            <ul className="space-y-3">
                                {solution.points.map((point, idx) => (
                                    <li key={idx} className="flex items-center">
                                        <div className={`flex-shrink-0 bg-gradient-to-r ${solution.color} opacity-20 p-1.5 rounded-full mr-3`}>
                                            <div className="w-4 h-4 text-white">
                                                <ChevronRight />
                                            </div>
                                        </div>
                                        <span className="text-sm text-slate-300">{point}</span>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Tags */}
                        {solution.tags && solution.tags.length > 0 && (
                            <div className="flex flex-wrap gap-3 pt-4">
                                {solution.tags.map((tag, idx) => (
                                    <div
                                        key={idx}
                                        className={`flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r ${solution.color} bg-opacity-10 text-white text-sm hover:bg-opacity-20 transition-all duration-300`}
                                    >
                                        {React.cloneElement(tag.icon, { className: "w-4 h-4" })}
                                        <span>{tag.text}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Right SVG */}
                {solution.ProcessSVG && (
                    <div className="w-[40%] flex items-center justify-center relative">
                        <div className="flex items-center justify-center w-full h-full">
                            <div className="transform-gpu opacity-90 scale-125">
                                <solution.ProcessSVG className="w-full h-full" />
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const AutomationSolutions = () => {
    const [activeSolution, setActiveSolution] = useState(0);
    const [isPaused, setIsPaused] = useState(false);
    const timeoutRef = useRef(null);

    // Auto-advance the active solution every 7 seconds
    useEffect(() => {
        if (!isPaused) {
            timeoutRef.current = setTimeout(() => {
                setActiveSolution((prev) => (prev + 1) % solutions.length);
            }, 7000);
        }

        return () => clearTimeout(timeoutRef.current);
    }, [activeSolution, isPaused]);

    // Handle navigation to other pages
    const handleNavigate = (path) => {
        console.log("Navigating to:", path);
        // Implement actual navigation logic here
    };

    // Handle hover over a solution
    const handleMouseEnter = (index) => {
        setActiveSolution(index);
        setIsPaused(true);
    };

    // Handle mouse leave
    const handleMouseLeave = () => {
        setIsPaused(false);
    };

    return (
        <section className="relative z-10 py-24 px-4 overflow-hidden">
            {/* Background effects */}
            <div className="absolute inset-0 bg-gradient-to-b from-blue-900/5 to-purple-900/5"></div>

            <div className="max-w-7xl mx-auto relative">
                <div className="text-center mb-16">
                    <div className="inline-block px-4 py-1.5 bg-gradient-to-r from-blue-900/30 to-purple-900/30 rounded-full text-blue-400 text-sm font-medium mb-4 border border-blue-700/30">
                        <div className="flex items-center gap-2">
                            <Bot className="w-4 h-4" />
                            <span>Process Optimization</span>
                            <span className="mx-1">•</span>
                            <span>Enterprise Automation</span>
                            <Settings className="w-4 h-4 text-purple-400" />
                        </div>
                    </div>
                    <h2 className="text-4xl md:text-6xl font-bold mb-6 leading-tight max-w-4xl mx-auto">
                        <span className="text-white">Intelligent Automation for </span>
                        <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent animate-gradient-x">Every Business Process</span>
                    </h2>
                    <p className="text-xl text-slate-300 leading-relaxed mb-10 max-w-3xl mx-auto">
                        Transform manual operations into streamlined automated workflows with our cutting-edge automation platform.
                        We implement intelligent solutions that scale, adapt, and deliver measurable efficiency gains.
                    </p>

                    {/* Solution indicator */}
                    <div className="inline-flex items-center gap-3 px-4 py-2 bg-slate-800/80 rounded-full border border-slate-700/50 mb-8">
                        {solutions.map((solution, idx) => (
                            <button
                                key={idx}
                                className={`w-3 h-3 rounded-full transition-all duration-300 ${activeSolution === idx ? 'bg-gradient-to-r ' + solution.color + ' shadow-md ' + solution.shadowColor : 'bg-slate-600'}`}
                                onClick={() => setActiveSolution(idx)}
                                aria-label={`View ${solution.title}`}
                            ></button>
                        ))}
                    </div>
                </div>

                {/* Solutions Content */}
                <div className="relative max-w-6xl mx-auto" onMouseLeave={handleMouseLeave}>
                    {/* Pipeline visualization */}
                    <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-10 bg-gradient-to-b from-slate-800 via-slate-800 to-slate-800 transform -translate-x-1/2 rounded-full border-l border-r border-blue-500/30">
                        {/* Pipeline segments */}
                        {[...Array(12)].map((_, i) => (
                            <div
                                key={i}
                                className="absolute w-full h-6 border-t border-b border-blue-500/30"
                                style={{ top: `${i * 8}%` }}
                            ></div>
                        ))}

                        {/* Data flow animation */}
                        <PipelineFlow active={true} />
                    </div>

                    {/* Solution Cards */}
                    <div className="space-y-24 relative z-10">
                        {solutions.map((solution, index) => (
                            <div key={solution.id} className="relative">
                                {/* Connector to pipeline - horizontal connector */}
                                <div className="hidden md:block absolute top-1/2 -left-14 h-8 w-20 bg-gradient-to-r from-slate-800 to-slate-800 transform -translate-y-1/2 rounded-full border border-blue-500/30">
                                    {/* Connector segments */}
                                    {[...Array(3)].map((_, i) => (
                                        <div
                                            key={i}
                                            className="absolute h-full w-4 border-l border-r border-blue-500/30"
                                            style={{ left: `${i * 25}%` }}
                                        ></div>
                                    ))}

                                    {/* Data flow in connector */}
                                    {activeSolution === index && (
                                        <div className="absolute inset-0 overflow-hidden">
                                            {[...Array(5)].map((_, i) => (
                                                <div
                                                    key={i}
                                                    className="absolute h-2 w-2 rounded-full bg-gradient-to-r from-blue-400 to-purple-400 animate-flow-right"
                                                    style={{
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        left: '-10%',
                                                        animationDelay: `${i * 0.5}s`,
                                                        animationDuration: '2s'
                                                    }}
                                                ></div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                {/* Pipeline node indicator for active solution */}
                                {activeSolution === index && (
                                    <div className="hidden md:flex absolute -left-24 top-1/2 transform -translate-y-1/2 items-center justify-center">
                                        <div className={`relative w-16 h-16 rounded-full border-4 border-slate-800 ${solution.shadowColor.replace('/20', '/50')} flex items-center justify-center overflow-hidden`}>
                                            <div className={`absolute inset-0 bg-gradient-to-r ${solution.color} opacity-20`}></div>
                                            <div className={`w-12 h-12 rounded-full bg-gradient-to-r ${solution.color} flex items-center justify-center animate-pulse`}>
                                                <Settings className="w-8 h-8 text-white" />
                                            </div>
                                        </div>
                                        <div className="absolute w-4 h-10 bg-slate-800 rounded-full border border-blue-500/30 flex items-center justify-center">
                                            <div className="w-1 h-6 bg-blue-500/30 rounded-full"></div>
                                        </div>
                                    </div>
                                )}

                                <div className={`transform transition-all duration-500 ${activeSolution === index ? 'scale-100 opacity-100' : 'scale-95 opacity-70'}`}>
                                    <SolutionCard
                                        solution={solution}
                                        active={activeSolution === index}
                                        onNavigate={handleNavigate}
                                        onMouseEnter={() => handleMouseEnter(index)}
                                    />
                                </div>

                                {/* Progress indicator */}
                                {activeSolution === index && (
                                    <div className="absolute -right-4 bottom-10 hidden md:block">
                                        <div className="relative h-24 w-2 bg-slate-700/50 rounded-full overflow-hidden">
                                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-blue-500 to-purple-500 animate-progress rounded-full" style={{ animationDuration: "7s" }}></div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {/* Action button */}
                    <div className="flex justify-center mt-16">
                        <button
                            className="bg-gradient-to-r from-blue-800 to-blue-900 hover:from-blue-900 hover:to-blue-950 text-white px-8 py-4 rounded-full font-medium flex items-center gap-2 transform transition-transform hover:scale-105 shadow-lg">
                            <span>Explore Automation Solutions</span>
                            <Bot className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default AutomationSolutions;